/**
 * API for client data export (async zip of documents + summary PDFs per year).
 * Start an export via POST; poll status via GET to obtain download URL when complete.
 */
import axios from "axios";
import { backend_url } from "@/constants";
import { AXIOS_CONFIG } from "@/utils/api";
import { Endpoints } from "./endpoints";

const CLIENT_EXPORT_URL = `${backend_url}/${Endpoints.clientExport}`;
const CLIENT_EXPORT_STATUS_URL = `${backend_url}/${Endpoints.clientExportStatus}`;

/** Request body for starting a client export. */
export interface StartClientExportRequest {
  clientId?: string;
  taxYear?: number;
  taxYears?: number[];
}

/** Response from POST api/forms/export/ (202 Accepted). */
export interface StartClientExportResponse {
  jobId: string;
  exportId: number;
  skippedYears?: number[];
  message?: string;
}

/** Response from GET api/forms/export/status/. */
export interface ClientExportStatusResponse {
  exportId: number;
  jobId: string;
  status: "pending" | "in_progress" | "completed" | "failed";
  taxYears: number[];
  createdAt: string | null;
  updatedAt: string | null;
  expiresAt: string | null;
  expired: boolean;
  downloadUrl?: string;
  message?: string;
  skippedYears?: number[];
}

/**
 * Start a client data export. Caller must be the client (omit clientId) or a tax pro with access.
 * @param params - Optional clientId and either taxYear or taxYears; omit both for "all years".
 * @returns 202 response with jobId and exportId.
 */
export async function startClientExport(
  params: StartClientExportRequest = {}
): Promise<StartClientExportResponse> {
  const body: Record<string, unknown> = {};
  if (params.clientId !== undefined) body.clientId = params.clientId;
  if (params.taxYear !== undefined) body.taxYear = params.taxYear;
  if (params.taxYears !== undefined) body.taxYears = params.taxYears;

  try {
    const response = await axios.post(CLIENT_EXPORT_URL, body, AXIOS_CONFIG);
    if (response.status !== 202) {
      throw new Error(response.data?.message ?? "Failed to start export");
    }
    return response.data.content as StartClientExportResponse;
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.data?.message) {
      throw new Error(err.response.data.message);
    }
    throw err;
  }
}

/**
 * Get client export status and optional download URL (when completed and not expired).
 * @param params - Provide either exportId or jobId.
 */
export async function getClientExportStatus(params: {
  exportId?: number;
  jobId?: string;
}): Promise<ClientExportStatusResponse> {
  const query = new URLSearchParams();
  if (params.exportId !== undefined)
    query.set("exportId", String(params.exportId));
  if (params.jobId !== undefined) query.set("jobId", params.jobId);
  const url = `${CLIENT_EXPORT_STATUS_URL}?${query.toString()}`;

  const response = await axios.get(url, AXIOS_CONFIG);
  if (response.status !== 200) {
    throw new Error(response.data?.message ?? "Failed to get export status");
  }
  return response.data.content as ClientExportStatusResponse;
}
